import React, { FC, useCallback } from "react";

import ContentTitle from "../../common/F7/ContentTitle";

import FormFields from "../../common/form/FieldsList";
import FormButtons from "../../common/form/Buttons";
import F7 from "../../../utils/F7";
import { useNavigate } from "react-router-dom";
import { useForm } from "../../../hooks/useForm";
import { ProjectField } from "../../../hooks/database/types/projects";
import { FieldType } from "../../../hooks/database/types/common";
import { useCurrentInventoryFormFieldId } from "../../../hooks/url";

const FieldTypes = ["text", "textarea", "number", "checkbox", "time", "date", "dropdown", "image", "link", "combobox", "löpnummer"].sort();

const generateFieldIdFromName = (fieldName: string) => {
  if (!fieldName) {
    return fieldName;
  }
  return fieldName
    .toLowerCase()
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/~/g, "")
    .replace(/[^a-z\d]/g, "_");
};

const EditFormField: FC<{ onFieldSave?: (field: ProjectField) => void; fields: Record<string, ProjectField> }> = (props) => {
  const { field: fieldName } = useCurrentInventoryFormFieldId();
  const field = fieldName ? props.fields[fieldName] : undefined;

  const { fields, handleChange, handleBlur, values } = useForm({
    label: {
      label: "Field Name",
      type: "text",
      value: field?.label || "",
      required: true,
    },
    type: {
      label: "Field Type",
      type: "dropdown",
      options: FieldTypes,
      value: field?.type || FieldTypes[0],
      required: true,
    },
    options: {
      label: "Options",
      type: "textarea",
      value: field?.options || "",
    },
    required: {
      label: "Required field",
      type: "checkbox",
      value: field?.required || false,
    },
  });

  const navigate = useNavigate();

  const handleSave = useCallback(() => {
    let label = values.label as string;
    if (!label || label.length < 1) {
      F7.alert("", "Please write a Field Name");
      return;
    } else if (label.length > 0 && label.charCodeAt(0) === 32) {
      F7.alert("", "Field Name can not start with a blankspace");
      return;
    }
    const splitted = label.split("~");
    let parseKey = label;
    if (splitted) {
      parseKey = splitted[0] ? splitted[0] : label;
      label = splitted[1] ? splitted[1] : label;
    }

    const fieldKey = field ? field.key : generateFieldIdFromName(parseKey);
    if (!fieldKey || fieldKey.length < 1) {
      F7.alert("", "Please write a Field Name");
      return;
    } else if (fieldKey.length > 0 && fieldKey.charCodeAt(0) === 32) {
      F7.alert("", "Field Name can not start with a blankspace");
      return;
    }
    if (props.onFieldSave) {
      props.onFieldSave({
        key: fieldKey,
        label: label,
        type: values.type as FieldType,
        options: values.options as string | null | undefined,
        required: values.required as boolean,
      });
    }
    navigate(-1);
  }, [values.label, values.type, values.options, values.required, field, navigate, props]);

  const handleCancel = useCallback(() => {
    navigate("../");
  }, [navigate]);

  return (
    <div>
      <ContentTitle>Field settings</ContentTitle>

      <FormFields fields={fields} onChange={handleChange} onBlur={handleBlur} />
      <FormButtons saveText="Save field" onSave={handleSave} onCancel={handleCancel} />
    </div>
  );
};

export default EditFormField;
